import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { toast } from '@/stores/toast';
import { ApiClientError } from '@/lib/api';
import { fetchPublishDetail, postPublish, postUnpublish, postMarkNotApplicable } from '@/lib/api/publish';
import { DATA_TYPE_LABELS, isVisibleStatus } from '@/lib/types-publish-gate';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type Props = {
  open: boolean;
  nodeId: string;
  nodeName: string;
  targetMonth: string;
  onClose: () => void;
};

/**
 * v0.3 (S-7): セル詳細モーダル。
 * Design H3対応: スコープ過大を避けるため「状態 + 主要CTA」を主表示にし、履歴/AIコメントは
 * accordion (default折り畳み) に格納 (詳細ページへのルート分離の代替、より低コスト)。
 */
export const PublishCellDetailModal = ({ open, nodeId, nodeName, targetMonth, onClose }: Props) => {
  const qc = useQueryClient();
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['publish-detail', nodeId, targetMonth],
    queryFn: () => fetchPublishDetail(nodeId, targetMonth),
    enabled: open,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['publish-status'] });
    qc.invalidateQueries({ queryKey: ['publish-detail', nodeId, targetMonth] });
  };

  const publishMut = useMutation({
    mutationFn: (status: 'preliminary' | 'published') => postPublish({ node_id: nodeId, target_month: targetMonth, allow_partial: true, status }),
    onSuccess: (res) => {
      invalidateAll();
      const label = res.status === 'preliminary' ? '速報公開' : '公開';
      toast.success(res.partial_publish ? `一部未入力のまま${label}しました` : `${label}しました`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '公開に失敗しました'),
  });

  const unpublishMut = useMutation({
    mutationFn: () => postUnpublish({ node_id: nodeId, target_month: targetMonth }),
    onSuccess: () => { invalidateAll(); toast.success('非公開にしました'); setConfirmUnpublish(false); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '非公開化に失敗しました'); setConfirmUnpublish(false); },
  });

  const naMut = useMutation({
    mutationFn: (dataType: string) => postMarkNotApplicable({ node_id: nodeId, data_type: dataType, scope: 'spot', target_month: targetMonth }),
    onSuccess: () => { invalidateAll(); toast.success('対象外に設定しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '対象外設定に失敗しました'),
  });

  const canPublish = hasPermissionForFeatureAction('publish', 'publish');
  const canUnpublish = hasPermissionForFeatureAction('publish', 'unpublish');
  const canMarkNotApplicable = hasPermissionForFeatureAction('publish', 'mark_not_applicable');
  const status = data?.publish_status?.status ?? 'draft';

  return (
    <Modal
      open={open}
      title={`${nodeName} ${targetMonth.slice(0, 4)}年${targetMonth.slice(5, 7)}月`}
      onClose={onClose}
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose}
            className="h-10 px-5 rounded-md bg-white text-text-body text-[13px] font-extrabold btn-press-secondary hover:bg-surface-sunken focus-ring min-h-[44px] sm:min-h-[40px]">
            閉じる
          </button>
          {/* v0.4 章⑤ (D-79): isVisibleStatus (published/preliminary) なら非公開化ボタンを出す
              (§9-3 が指摘した「preliminary で非公開化ボタンが出ない」バグの解消) */}
          {isVisibleStatus(status) && canUnpublish && (
            <button type="button" onClick={() => setConfirmUnpublish(true)} disabled={unpublishMut.isPending}
              className="h-10 px-5 rounded-md bg-white text-danger-text border border-danger-border text-[13px] font-extrabold btn-press-secondary hover:bg-danger-bg disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
              非公開にする
            </button>
          )}
          {status === 'draft' && canPublish && (
            // ⚠️ design-and-code MEDIUM 指摘 (D-186、StatusIcon.tsx と同型): misespark-orange は
            // hover:bg-alert-bg 到達時に 4.5:1 コントラストを満たさない。「非公開にする」ボタンの
            // danger-text/danger-border/danger-bg と同じ alert.* トークンの組に揃える。
            <button type="button" onClick={() => publishMut.mutate('preliminary')} disabled={publishMut.isPending}
              className="h-10 px-5 rounded-md bg-white text-alert-text border border-alert-border text-[13px] font-extrabold btn-press-secondary hover:bg-alert-bg disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
              {publishMut.isPending ? '処理中…' : '速報公開する'}
            </button>
          )}
          {status !== 'published' && canPublish && (
            <button type="button" onClick={() => publishMut.mutate('published')} disabled={publishMut.isPending}
              className="h-10 px-5 rounded-md bg-brand-red text-white text-[13px] font-extrabold btn-press-primary hover:bg-brand-red-hover disabled:opacity-45 focus-ring min-h-[44px] sm:min-h-[40px]">
              {publishMut.isPending ? '公開中…' : status === 'preliminary' ? '確定公開する' : '公開する'}
            </button>
          )}
        </>
      }
    >
      {isLoading || !data ? (
        <p className="text-[13px] text-text-sub">読み込み中…</p>
      ) : (
        <div className="space-y-4">
          <section>
            <h3 className="text-[12px] font-extrabold text-text-sub mb-2">データ種別ごとの入力状況</h3>
            <ul className="space-y-1">
              {data.input_status.length === 0 && (
                <li className="text-[13px] text-text-muted">まだ入力データがありません</li>
              )}
              {data.input_status.map((s) => (
                <li key={s.inputStatusId} className="flex items-center justify-between rounded bg-surface-sunken px-3 py-2 gap-2">
                  <span className="text-[13px] text-text-body">{DATA_TYPE_LABELS[s.dataType] ?? s.dataType}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[12px] font-bold ${s.inputReady ? 'text-success-text' : 'text-text-muted'}`}>
                      {s.inputReady ? '入力完了' : '未完了'}
                    </span>
                    {!s.inputReady && canMarkNotApplicable && (
                      <button type="button" onClick={() => naMut.mutate(s.dataType)} disabled={naMut.isPending}
                        className="text-[11px] text-text-sub underline hover:text-text-main focus-ring disabled:opacity-45 min-h-[32px]">
                        今月は対象外にする
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {data.not_applicable.length > 0 && (
            <section>
              <h3 className="text-[12px] font-extrabold text-text-sub mb-2">対象外宣言</h3>
              <ul className="space-y-1">
                {data.not_applicable.map((na) => (
                  <li key={na.declarationId} className="text-[13px] text-text-body">
                    {DATA_TYPE_LABELS[na.dataType] ?? na.dataType} — {na.scope === 'permanent' ? '恒久的に対象外' : `${na.targetMonth} のみ対象外`}
                    {na.note && <span className="text-text-muted"> ({na.note})</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.ai_comment && (
            <section>
              <h3 className="text-[12px] font-extrabold text-text-sub mb-2">AIコメント</h3>
              {data.ai_comment.status === 'succeeded' && <p className="text-[13px] text-text-body whitespace-pre-wrap">{data.ai_comment.commentBody}</p>}
              {(data.ai_comment.status === 'pending' || data.ai_comment.status === 'generating') && (
                <p className="text-[13px] text-text-sub">生成中…</p>
              )}
              {data.ai_comment.status === 'failed' && (
                <p className="text-[13px] text-danger-text">生成に失敗しました: {data.ai_comment.errorMessage}</p>
              )}
            </section>
          )}

          <section>
            <button type="button" onClick={() => setHistoryOpen((v) => !v)}
              className="flex items-center gap-1 text-[12px] font-extrabold text-text-sub focus-ring rounded min-h-[36px]">
              {historyOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              変更履歴 ({data.history.length}件)
            </button>
            {historyOpen && (
              <ul className="mt-2 space-y-1 border-l-2 border-surface-line pl-3">
                {data.history.map((h, i) => (
                  <li key={i} className="text-[12px] text-text-sub">
                    {h.effective_from.slice(0, 16).replace('T', ' ')} — {h.status === 'published' ? '公開' : h.status === 'preliminary' ? '速報公開' : '下書き'}
                    {h.published_by_name && ` (${h.published_by_name})`}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      <ConfirmDialog
        open={confirmUnpublish}
        title="非公開にする"
        destructive
        confirmLabel="非公開にする"
        loading={unpublishMut.isPending}
        onClose={() => setConfirmUnpublish(false)}
        onConfirm={() => unpublishMut.mutate()}
        message={`${nodeName} ${targetMonth} を非公開に戻します。閲覧権限のないユーザーからは再び見えなくなります。`}
      />
    </Modal>
  );
};
